/** @file RenameField: the in-row name field (Enter commits, Esc cancels, blur commits, inline error). */
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

/** `onSubmit` resolves true when the field may close (renamed, unchanged, or abandoned on blur); false keeps it open. */
export type RenameFieldProps = { initial: string; error: string | null; onSubmit: (value: string, viaBlur: boolean) => Promise<boolean>; onCancel: () => void };

/** A text field on `initial`, focused with its text selected; clicks and keys stay inside (no select, no framing). */
export function RenameField({ initial, error, onSubmit, onCancel }: RenameFieldProps): ReactElement {
  const ref = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const [value, setValue] = useState(initial);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const submit = (viaBlur: boolean): void => {
    if (busy.current) return;
    busy.current = true;
    void onSubmit(value, viaBlur).then((closed) => { if (!closed) busy.current = false; });
  };
  return (
    <>
      <input ref={ref} value={value} aria-label="Node name" aria-invalid={error !== null} spellCheck={false}
        style={{ font: 'inherit', width: 140, background: 'var(--field, #101216)', color: 'inherit', border: `1px solid ${error === null ? 'var(--accent, #f2b544)' : 'var(--error, #e06c6c)'}` }}
        onChange={(e) => { setValue(e.target.value); }}
        onClick={(e) => { e.stopPropagation(); }} onDoubleClick={(e) => { e.stopPropagation(); }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.code === 'Enter' || e.code === 'NumpadEnter') { e.preventDefault(); submit(false); }
          if (e.code === 'Escape') { e.preventDefault(); busy.current = true; onCancel(); }
        }}
        onBlur={() => { submit(true); }} />
      {error !== null && <span role="alert" style={{ color: 'var(--error, #e06c6c)', fontSize: 11, whiteSpace: 'normal' }}>{error}</span>}
    </>
  );
}
